import { CameraFilled, LeftOutlined } from '@ant-design/icons';
import {
  billsCount,
  cubicMeters,
  dirhams,
  isResultFor,
  LatePayersAlert,
  type MonthKey,
  MonthSwitch,
  metersCount,
  monthLabel,
  readingsStatus,
  useStoredMonth,
} from '@horof/shared';
import { Alert, Button, Collapse, Skeleton } from 'antd';
import { type ReactNode, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useQuery } from 'urql';
import { graphql } from '../gql';
import { LatePayersImages } from './LatePayersImages';
import {
  type MeterStatus,
  MonthCloseDocument,
  monthVariables,
} from './month-data';
import { Step } from './Step';
import { WhatsappStatus } from './WhatsappStatus';

const LatePayersDocument = graphql(`
  query LatePayers($audience: LateListAudience!) {
    latePayersImages(audience: $audience)
  }
`);

const refetchBills = { additionalTypenames: ['Bill'] };

/**
 * Where the month stands, to follow on the phone: readings, bills, WhatsApp
 * deliveries, and what is shared in the village groups. The close itself
 * (review, sending, printing) is done on the desk (docs/changes/43–44).
 */
export function MonthPage() {
  const [month, setMonth] = useStoredMonth('committee.month');
  const navigate = useNavigate();
  const variables = useMemo(() => monthVariables(month), [month]);
  const [{ data, error, operation }, refetch] = useQuery({
    query: MonthCloseDocument,
    variables,
    context: refetchBills,
  });
  // Right after a month change urql still returns the previous month's
  // result: dim it and block its buttons (e.g. WhatsApp sending) until the
  // new month is there.
  const switching = Boolean(data) && !isResultFor(operation, variables);

  const readings = useMemo(
    () => readingsStatus(data?.meters?.nodes ?? []),
    [data],
  );
  // monthlyReport also returns a row for a month without bills (count 0).
  const billed = data?.report?.nodes[0];
  const report = billed && billed.billsCount > 0 ? billed : null;
  const high = (data?.highConsumption?.nodes ?? []).flatMap((b) =>
    b ? [b] : [],
  );
  const read = readings.total - readings.unread.length;

  return (
    <div className="page">
      <header className="month-header">
        <div className="app-title">حالة الشهر</div>
        <MonthSwitch month={month} onChange={setMonth} loading={switching} />
      </header>
      <LatePayersAlert onOpen={() => navigate('/reports/arrears')} />

      {error && (
        <Alert
          type="error"
          showIcon
          message="تعذّر تحميل الشهر"
          action={
            <Button onClick={() => refetch({ requestPolicy: 'network-only' })}>
              أعد المحاولة
            </Button>
          }
        />
      )}
      {!data && !error && <Skeleton active paragraph={{ rows: 10 }} />}

      {data && (
        <div className={`month-steps${switching ? ' is-switching' : ''}`}>
          <Step
            n={1}
            title="القراءات"
            state={readings.unread.length ? 'warn' : 'done'}
            summary={
              <>
                <span className="ltr-num big-count">
                  {read} / {readings.total}
                </span>{' '}
                {readings.unread.length
                  ? `— ${metersCount(readings.unread.length)} بدون قراءة`
                  : '— كل العدادات مقروءة'}
              </>
            }
          >
            {readings.unread.length > 0 && (
              <MeterList
                title="العدادات غير المقروءة"
                meters={readings.unread}
              />
            )}
          </Step>

          <Step
            n={2}
            title="الفواتير"
            state={
              readings.duplicates.length ? 'warn' : report ? 'done' : 'todo'
            }
            summary={
              report ? (
                <>
                  {billsCount(report.billsCount)} ·{' '}
                  {cubicMeters(report.consumption)} ·{' '}
                  <strong>{dirhams(report.billedTotal)}</strong>
                </>
              ) : (
                'لا توجد فواتير لهذا الشهر بعد.'
              )
            }
          >
            {report && (
              <ul className="facts">
                <li>
                  استهلاك منعدم:{' '}
                  {metersCount(data.zeroConsumption?.totalCount ?? 0)}
                </li>
                {high.length > 0 && (
                  <li>
                    استهلاك يفوق 50 م³ (تحقّق من القراءة):
                    <ul>
                      {high.map((b) => (
                        <li key={b.id}>
                          <RowLink to={`/bills/${b.id}`}>
                            {b.readingPhoto && (
                              <CameraFilled
                                className="photo-icon"
                                aria-label="مع صورة العداد"
                              />
                            )}{' '}
                            {b.member?.fullName} ({b.meter?.label}):{' '}
                            <span className="ltr-num">
                              {b.previousReading} → {b.currentReading}
                            </span>{' '}
                            = {cubicMeters(b.consumption)}،{' '}
                            {dirhams(b.billTotal)}
                          </RowLink>
                        </li>
                      ))}
                    </ul>
                  </li>
                )}
              </ul>
            )}
            {readings.duplicates.length > 0 && (
              <Alert
                type="warning"
                showIcon
                message={`${metersCount(readings.duplicates.length)} بفاتورتين لهذا الشهر`}
                description="تُصحَّح من الحاسوب («مكتب الجمعية»، إغلاق الشهر)."
              />
            )}
          </Step>

          <WhatsappStatus n={3} start={variables.start} end={variables.end} />

          <SharingStep n={4} month={month} bulletinLink={data.bulletinLink} />
        </div>
      )}
    </div>
  );
}

/**
 * A whole row that opens a page (a big target on a phone), with ‹ showing it
 * leads somewhere. The meter page shows the member (with a link) and the
 * meter's latest bills: it is where a bill of this month is looked at.
 */
function RowLink({ to, children }: { to: string | null; children: ReactNode }) {
  if (!to) return <span>{children}</span>;
  return (
    <Link to={to} className="row-link">
      <span>{children}</span>
      <LeftOutlined className="row-link-arrow" />
    </Link>
  );
}

function MeterList({
  title,
  meters,
}: {
  title: string;
  meters: MeterStatus[];
}) {
  return (
    <Collapse
      size="large"
      items={[
        {
          key: 'meters',
          label: `${title} (${meters.length})`,
          children: (
            <ul className="plain-list">
              {meters.map((m) => (
                <li key={m.id}>
                  <RowLink to={`/meters/${m.id}`}>
                    <strong>{m.memberName}</strong> — العداد {m.label}
                  </RowLink>
                </li>
              ))}
            </ul>
          ),
        },
      ]}
    />
  );
}

function SharingStep({
  n,
  month,
  bulletinLink,
}: {
  n: number;
  month: MonthKey;
  bulletinLink: string;
}) {
  const [audience, setAudience] = useState<'PUBLIC' | 'COMMITTEE' | null>(null);
  // Asked for on demand: each image is rendered when opened.
  const [{ data, fetching, error }] = useQuery({
    query: LatePayersDocument,
    variables: { audience: audience ?? 'PUBLIC' },
    pause: audience === null,
  });
  const images = data?.latePayersImages ?? [];

  return (
    <Step
      n={n}
      title="النشر في مجموعات واتساب"
      state="todo"
      summary="افتح أو حمّل الملف والصور، ثم شاركها في المجموعة. على الهاتف: «تجهيز كل الصور» ثم «مشاركة الكل» لإرسالها دفعة واحدة."
    >
      <Button href={bulletinLink} target="_blank" size="large" block>
        النشرة الشهرية ({monthLabel(month)})
      </Button>
      <div className="link-grid">
        <Button
          size="large"
          type={audience === 'PUBLIC' ? 'primary' : 'default'}
          onClick={() => setAudience('PUBLIC')}
        >
          المتأخرون: المجموعة العامة
        </Button>
        <Button
          size="large"
          type={audience === 'COMMITTEE' ? 'primary' : 'default'}
          onClick={() => setAudience('COMMITTEE')}
        >
          المتأخرون: مجموعة اللجنة
        </Button>
      </div>
      {fetching && <Skeleton.Button active block />}
      {error && <Alert type="error" message="تعذّر إنشاء الصور" />}
      {audience && !fetching && images.length > 0 && (
        <LatePayersImages audience={audience} links={images} />
      )}
    </Step>
  );
}
